import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function auDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

const DISMISS_KEY = "tm_readonly_banner_dismissed";

export const ReadOnlyBanner = () => {
  const { readOnly, user } = useAuth();
  // Dismiss is "for the visit" only — sessionStorage clears when the tab closes.
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!readOnly || dismissed) return null;
  const endedOn = auDate(user?.trial?.expires_at);
  const keptUntil = auDate(user?.trial?.data_kept_until);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="read-only-banner"
      className="rounded-md border border-[#ECDCA6] bg-[#FBF4DD] px-3 py-3 sm:px-4 sm:py-3.5 mb-6 flex items-center gap-3"
    >
      <div className="w-8 h-8 rounded-md bg-[#F6ECC9] border border-[#ECDCA6] flex items-center justify-center shrink-0">
        <Lock className="w-4 h-4 text-[#7A5E18]" strokeWidth={2.4} />
      </div>

      <div className="min-w-0 flex-1">
        {/* Compact copy on phones: one line + a short second line. */}
        <div className="sm:hidden">
          <p className="text-sm font-semibold text-[#7A5E18] leading-snug">
            Trial ended — your account is read only.
          </p>
          <p className="text-xs text-[#6B635C] mt-0.5">
            View &amp; export only.{keptUntil ? ` Kept until ${keptUntil}.` : ""}
          </p>
        </div>

        {/* Full wording on tablet / desktop. */}
        <div className="hidden sm:block">
          <p className="text-sm font-semibold text-[#7A5E18]">
            {endedOn ? `Your trial ended on ${endedOn}. ` : "Your trial has ended. "}
            Your account is read only, you can still view and export everything.
          </p>
          <p className="text-xs text-[#6B635C] mt-1">
            Subscribe to keep working.{keptUntil ? ` Your data is kept until ${keptUntil}.` : ""}
          </p>
        </div>
      </div>

      <Link
        to="/settings#plan"
        data-testid="read-only-banner-subscribe"
        className="shrink-0 text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] transition-colors whitespace-nowrap"
      >
        Subscribe
      </Link>

      {/* Dismiss is a phone affordance only — desktop keeps the banner visible. */}
      <button
        type="button"
        onClick={dismiss}
        data-testid="read-only-banner-dismiss"
        aria-label="Dismiss"
        className="sm:hidden shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-[#6B635C] hover:text-[#2A2623] hover:bg-[#FBF4DD] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ReadOnlyBanner;
